import React, {Component} from 'react';
import {passCsrfToken} from '../../helpers';
import axios from 'axios';
import {Link} from 'react-router-dom';
import {Segment} from 'semantic-ui-react';
import moment from 'moment';
import {getServiceTransactions} from '../../api/serviceTransactionApi';
import {formatMoneyLegacy} from '../../utils/currency';
import {
	txDescription,
	ledgerTotals,
	openingBalanceBeforeMonth,
	balanceAfterForTransaction,
} from '../../utils/vendorLedger';
import MonthPickerGrid, {
	buildTransactionMonthCounts,
	filterTransactionsByMonth,
} from '../shared/MonthPickerGrid';
import '../admin/BookingCalendar.scss';

class ServiceTransactionList extends Component {
	constructor(props) {
		super(props);
		const now = new Date();
		this.state = {
			year: now.getFullYear(),
			selectedMonth: now.getMonth() + 1,
			yearTransactions: [],
			serviceTransactions: [],
			accountSummary: null,
			loading: false,
			directionFilter: null,
		};
	}

	componentDidMount() {
		passCsrfToken(document, axios);
		this.fetchYearTransactions();
	}

	fetchYearTransactions = (yearOverride) => {
		const year = yearOverride != null ? yearOverride : this.state.year;
		this.setState({ loading: true });
		getServiceTransactions({
			per_page: 500,
			q: {
				created_at_gteq: `${year}-01-01`,
				created_at_lteq: `${year}-12-31`,
			},
		})
			.then((response) => {
				const data = response.data;
				const list = (data && data.service_transactions) || (Array.isArray(data) ? data : []) || [];
				this.setState({
					yearTransactions: list,
					accountSummary: data?.account_summary || null,
					loading: false,
				}, this.applyFilters);
			})
			.catch((error) => {
				console.log('Transaction fetch error', error);
				this.setState({ yearTransactions: [], serviceTransactions: [], loading: false });
			});
	};

	applyFilters = () => {
		const { year, selectedMonth, yearTransactions, directionFilter } = this.state;
		let list = filterTransactionsByMonth(yearTransactions, year, selectedMonth);
		if (directionFilter) {
			list = list.filter((tx) => tx.direction === directionFilter);
		}
		list = [...list].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
		this.setState({ serviceTransactions: list });
	};

	handleYearChange = (year) => {
		const now = new Date();
		const selectedMonth = year === now.getFullYear() ? now.getMonth() + 1 : null;
		this.setState({ year, selectedMonth, directionFilter: null }, () => this.fetchYearTransactions(year));
	};

	handleMonthSelect = (month) => {
		this.setState({ selectedMonth: month, directionFilter: null }, this.applyFilters);
	};

	handleDirectionFilter = (directionFilter) => {
		this.setState({ directionFilter }, this.applyFilters);
	};

	renderBookingDetails = (transaction) => {
		const bd = transaction.booking_details;
		if (!bd || typeof bd !== 'object') return null;
		if (bd.type === 'RENTAL' && bd.idx) {
			return <Link to={`/partner/rental_details/${bd.idx}`}>View rental</Link>;
		}
		if (bd.type === 'PACKAGE' && bd.idx) {
			return <Link to={`/partner/package_details/${bd.idx}`}>View tour</Link>;
		}
		if (bd.group_tour_idx) {
			return <span className="text-muted small">Group trip</span>;
		}
		return null;
	};

	renderSummaryCards = (byCurrency) => {
		const entries = Object.entries(byCurrency || {});
		if (entries.length === 0) return null;

		return (
			<div className="row mb-3">
				{entries.map(([cc, row]) => {
					const earned = row.credit != null ? row.credit : 0;
					const paid = row.debit != null ? row.debit : 0;
					const owed = row.due != null ? row.due : Math.max(0, earned - paid);
					return (
						<div key={cc} className="col-12 mb-2">
							{entries.length > 1 && (
								<div className="small text-muted font-weight-bold mb-1">{cc}</div>
							)}
							<div className="row text-center">
								<div className="col-4">
									<div className="border rounded p-2 h-100 bg-white">
										<div className="small text-muted">Total earned</div>
										<div className="font-weight-bold text-success">{formatMoneyLegacy(earned, cc)}</div>
										<div className="small text-muted">Credits from trips</div>
									</div>
								</div>
								<div className="col-4">
									<div className="border rounded p-2 h-100 bg-white">
										<div className="small text-muted">Total paid to you</div>
										<div className="font-weight-bold text-primary">{formatMoneyLegacy(paid, cc)}</div>
										<div className="small text-muted">Debits / payouts</div>
									</div>
								</div>
								<div className="col-4">
									<div className={`border rounded p-2 h-100 ${owed > 0 ? 'bg-light border-warning' : 'bg-white'}`}>
										<div className="small text-muted">Still owed to you</div>
										<div className={`font-weight-bold ${owed > 0 ? 'text-warning' : 'text-success'}`}>
											{formatMoneyLegacy(owed, cc)}
										</div>
										<div className="small text-muted">{owed > 0 ? 'Awaiting payout' : 'All paid up'}</div>
									</div>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	};

	render() {
		const {
			year,
			selectedMonth,
			yearTransactions,
			serviceTransactions,
			accountSummary,
			loading,
			directionFilter,
		} = this.state;
		const monthCounts = buildTransactionMonthCounts(yearTransactions, year);
		const monthLabel = selectedMonth
			? moment(`${year}-${String(selectedMonth).padStart(2, '0')}-01`).format('MMMM YYYY')
			: null;
		const byCurrency = accountSummary?.by_currency || {};
		const monthTotals = ledgerTotals(serviceTransactions);
		const displayCc = serviceTransactions[0]?.currency_code
			|| Object.keys(byCurrency)[0]
			|| 'USD';
		const openingBalance = selectedMonth
			? openingBalanceBeforeMonth(yearTransactions, year, selectedMonth)
			: 0;

		return (
			<div className="ui container">
				<div className="mb-3">
					<h3 className="title mb-1">My payments</h3>
					<p className="text-muted small mb-2">
						Like a bank statement: <strong>Opening balance</strong> carries forward from earlier months; <strong>Credit</strong> adds what you earned; <strong>Debit</strong> subtracts payouts; <strong>Balance after</strong> is the running total.
					</p>
					{accountSummary && this.renderSummaryCards(byCurrency)}
				</div>

				<MonthPickerGrid
					year={year}
					onYearChange={this.handleYearChange}
					selectedMonth={selectedMonth}
					onMonthSelect={this.handleMonthSelect}
					monthCounts={monthCounts}
					loading={loading}
					hint="Click a month to see credits and debits for that month."
				/>

				{selectedMonth && (
					<>
						<div className="d-flex flex-wrap align-items-center justify-content-between mb-2 mt-3 gap-2">
							<div>
								<h4 className="h6 mb-0">{monthLabel}</h4>
								<p className="small text-muted mb-0">
									This month: earned {formatMoneyLegacy(monthTotals.credit, displayCc)}
									{' · '}paid {formatMoneyLegacy(monthTotals.debit, displayCc)}
								</p>
							</div>
							<div className="btn-group btn-group-sm" role="group" aria-label="Filter by type">
								<button
									type="button"
									className={`btn btn-outline-secondary${!directionFilter ? ' active' : ''}`}
									onClick={() => this.handleDirectionFilter(null)}
								>
									All
								</button>
								<button
									type="button"
									className={`btn btn-outline-secondary${directionFilter === 'credit' ? ' active' : ''}`}
									onClick={() => this.handleDirectionFilter('credit')}
								>
									Credits
								</button>
								<button
									type="button"
									className={`btn btn-outline-secondary${directionFilter === 'debit' ? ' active' : ''}`}
									onClick={() => this.handleDirectionFilter('debit')}
								>
									Debits
								</button>
							</div>
						</div>

						<Segment className="p-0">
							{serviceTransactions.length === 0 && openingBalance === 0 ? (
								<p className="text-muted p-3 mb-0">No entries this month.</p>
							) : (
								<div className="table-responsive">
									<table className="table table-striped table-bordered mb-0">
										<thead>
											<tr>
												<th>Date</th>
												<th>Type</th>
												<th>Description</th>
												<th className="text-right">Amount</th>
												<th className="text-right">Balance after</th>
											</tr>
										</thead>
										<tbody>
											<tr className="table-light">
												<td colSpan={3}>
													<em>Opening balance</em>
													<span className="text-muted small d-block">Before {monthLabel}</span>
												</td>
												<td className="text-right text-muted">—</td>
												<td className="text-right font-weight-bold">
													{formatMoneyLegacy(openingBalance, displayCc)}
												</td>
											</tr>
											{serviceTransactions.map((transaction, index) => {
												const cc = transaction.currency_code || displayCc;
												const isCredit = transaction.direction === 'credit';
												const balance = balanceAfterForTransaction(transaction, yearTransactions);
												const details = this.renderBookingDetails(transaction);
												return (
													<tr key={transaction.idx || index}>
														<td>{moment(transaction.created_at).format('D MMM YYYY')}</td>
														<td>
															<span className={`badge badge-${isCredit ? 'success' : 'primary'}`}>
																{isCredit ? 'Credit' : 'Debit'}
															</span>
														</td>
														<td>
															{txDescription(transaction)}
															{details && <div className="mt-1">{details}</div>}
														</td>
														<td className={`text-right font-weight-bold ${isCredit ? 'text-success' : 'text-primary'}`}>
															{isCredit ? '+' : '−'}
															{formatMoneyLegacy(transaction.amount, cc)}
														</td>
														<td className="text-right">
															{formatMoneyLegacy(balance, cc)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
						</Segment>
					</>
				)}
			</div>
		);
	}
}

export default ServiceTransactionList;
